import * as React from 'react'
import { Select, message } from 'antd'
import { Modal, Row, Col, Input } from 'antd-v4'
import {
  LicensedProducerAPI,
  ILicensedProducerContract,
  BrandAPI,
  IBrandInfoContract,
} from '@getgreenline/homi-shared'
import { inject } from 'mobx-react'
import { CurrentCompanyStore } from '../../../stores/CurrentCompanyStore'
import { parseErrorMsg } from '../../../utilities/helpers'
import { Track } from '../../../Track'

interface Props {
  isModalVisible: boolean
  selectedBrand?: IBrandInfoContract
  closeModal: () => void
  getBrands: VoidFunction
}

interface InjectedProps {
  currentCompanyStore?: CurrentCompanyStore
}

export const SubmitBrandModal = inject('currentCompanyStore')((props: Props & InjectedProps) => {
  const { isModalVisible, selectedBrand, closeModal, getBrands, currentCompanyStore } = props

  const company = currentCompanyStore!.company!

  const [name, setName] = React.useState<string>(selectedBrand ? selectedBrand.name : '')
  const [licensedProducerId, setLicensedProducerId] = React.useState<number | null>(
    selectedBrand ? selectedBrand.licensedProducerId : null,
  )
  const [licensedProducers, setLicensedProducers] = React.useState<ILicensedProducerContract[]>([])
  const [isLoading, setIsLoading] = React.useState(false)

  React.useEffect(() => {
    ;(async () => {
      const lps = await LicensedProducerAPI.getLicensedProducers(company.id)

      setLicensedProducers(lps)
    })()
  }, [])

  const submitBrand = async () => {
    const brandName = name.trim()

    if (brandName.length === 0) {
      return message.error('A brand name is required')
    }

    setIsLoading(true)

    try {
      if (selectedBrand) {
        await BrandAPI.editBrand(company.id, {
          id: selectedBrand.id,
          name: brandName,
          licensedProducerId,
        })

        Track.track('dashboard:brand_edited', {})
      } else {
        await BrandAPI.addBrand(company.id, {
          name: brandName,
          licensedProducerId,
        })

        Track.track('dashboard:brand_added', {})
      }

      await getBrands()

      message.success(`${brandName} brand successfully submitted`)

      closeModal()
    } catch (error) {
      message.error(parseErrorMsg(error) || 'An error occurred while submitting a new brand')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Modal
      title={`${selectedBrand ? 'Edit' : 'Add'} brand`}
      centered
      maskClosable={false}
      visible={isModalVisible}
      onOk={submitBrand}
      confirmLoading={isLoading}
      onCancel={closeModal}
      okText='Save'
    >
      <Row className='d-flex align-items-center mb-3'>
        <Col span={8}>Brand</Col>
        <Col span={16}>
          <Input
            placeholder='Type new brand name'
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </Col>
      </Row>

      <Row className='d-flex align-items-center'>
        <Col span={8}>Licensed producer</Col>
        <Col span={16}>
          <Select<number | undefined>
            className='w-100'
            allowClear
            dropdownMatchSelectWidth={false}
            defaultValue={licensedProducerId || undefined}
            onChange={(value) => setLicensedProducerId(value === undefined ? null : value)}
          >
            {licensedProducers.map((licensedProducer, index) => (
              <Select.Option key={index} value={licensedProducer.id}>
                {licensedProducer.name}
              </Select.Option>
            ))}
          </Select>
        </Col>
      </Row>
    </Modal>
  )
})
